import { Main } from '@/components/layout/main'
import { PageHeader } from '@/components/layout/page-header'

import { columns } from './components/clients-columns'
import { ClientsDialogs } from './components/clients-dialogs'
import { ClientsPrimaryButtons } from './components/clients-primary-buttons'
import { DataTable } from '../../components/table'
import ClientsProvider from './context/client-context'
import { Client, clientListSchema } from './data/schema'
import { useTranslation } from 'react-i18next'
import { get_clients } from '@/api/client'
import { useEffect, useState } from 'react'
import { Skeleton } from '@/components/ui/skeleton';
import DataTableSkeleton from '@/components/ui/data-table-skeleton';
import { DataTableToolbar } from './components/data-table-toolbar'
import { useRouter, useSearch } from '@tanstack/react-router'
import { filterNonNullValues } from '@/lib/utils'
import { Users } from 'lucide-react'

export interface ClientsTableParams {
  page: number
  page_size: number
  ordering: string | null
  name: string | null
}

export default function Clients() {
  const i18n = useTranslation()
  const search = useSearch({ strict: false });
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [pagesCnt, setPagesCnt] = useState<number>(0);
  const [params, setParams] = useState<ClientsTableParams>({
    page: search?.page || 0,
    page_size: search?.page_size || 10,
    ordering: search?.ordering || null,
    name: search?.name || null,
  });
  const router = useRouter();

  // Sync params to router search params
  useEffect(() => {
    router.navigate({
      to: ".",
      search: filterNonNullValues({
        page: params.page,
        page_size: params.page_size,
        ordering: params.ordering,
        name: params.name,
      }),
      replace: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  // Handle client update in the table
  const handleClientUpdate = (updatedClient: Client) => {
    setClients(prevClients =>
      prevClients.map(c => c.id === updatedClient.id ? updatedClient : c)
    );
  };

  const handleRowClick = (row: Client) => {
    router.navigate({ to: `/clients/${row.id}` });
  };

  // Parse user list
  useEffect(() => {
    get_clients(params).then((resp) => {
      const clients_list = clientListSchema.parse(resp.results)
      setClients(clients_list)
      setPagesCnt(Math.ceil(resp.count / params.page_size))
      setLoading(false)
      setError(false)
    }).catch((_) => {
      setError(true)
      setLoading(false)
    });
  }, [params]);

  return (
    <ClientsProvider>
      <Main>
        <PageHeader
          leading={
            <span className="mt-1 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Users className="h-5 w-5" />
            </span>
          }
          title={i18n.t('clients_list')}
          description={i18n.t('clients_list_desc')}
          actions={loading ? <Skeleton className="h-9 w-32" /> : <ClientsPrimaryButtons />}
        />
        <div className='-mx-4 flex-1 overflow-auto px-4 py-1 lg:flex-row lg:space-x-12 lg:space-y-0'>
          {
            loading ? <DataTableSkeleton columns={5} rows={5} />
              : error ?
                <div className='py-8 text-center'>
                  {i18n.t('table_load_error')}
                </div>
                : <DataTable
                  data={clients}
                  columns={columns}
                  params={params}
                  setParams={setParams}
                  pages={pagesCnt}
                  ToolbarName={DataTableToolbar}
                  storageKey="clients-table"
                  onRowClick={handleRowClick}
                />
          }
        </div>
      </Main>

      <ClientsDialogs params={params} setParams={setParams} onClientUpdate={handleClientUpdate} />
    </ClientsProvider>
  )
}
